// src/lib/ops.ts — GM Ops console API (GE-02P v2)
// Every read and write goes through the ops_* Postgres functions, which check the
// caller's operator role on the server. The console never reads or writes tables
// directly and never computes prices — it shows what ops_* returns.

import { supabase } from "./supabase";

export type Role = "owner" | "pricing_admin" | "viewer";
export type Tier = "aprendiz" | "oficial" | "maestro";   // MDO-9 P3: pay keys = títulos
export type Region = "pnw" | "norcal";

export interface WhoAmI {
  email: string | null;
  role: Role | null;
}

export interface Settings {
  card_fee_pct: number;
  card_fee_fixed_cents: number;
  reference_zips: string[];
  target_margin_pct: number;
  margin_floor_pct: number;
  rate_titulo_aprendiz_cents: number;
  rate_titulo_oficial_cents: number;
  rate_titulo_maestro_cents: number;
  mobilization_cents: number;
  price_endings: number[];
  /** GM-PRICE-02: ladder premium per job hour at 2 stories (also the always-on rate of tree wraps). */
  ladder_premium_cents_per_hr?: number | null;
  /** GM-PRICE-02: ladder premium per job hour at 3+ stories. */
  ladder_premium_3_cents_per_hr?: number | null;
  /** SR-B2: smallest cart (Lit Up) order; null = no minimum. */
  cart_visit_minimum_cents?: number | null;
  /** HSC-01: minimum for single bookings (one service, one payment) — on/off and amount. */
  single_visit_minimum_enabled?: boolean;
  single_visit_minimum_cents?: number;
  updated_at?: string | null;
  updated_by_email?: string | null;
}

export interface Economics {
  basis: "hours" | "legacy_rate" | "estimated_44" | "variant_hours" | "variant_no_hours";
  estimated: boolean;
  price_cents: number;
  fee_cents: number;
  kit_cents: number;
  other_cents: number;
  allowance_cents: number;
  mobilization_cents: number;
  payout_cents: Record<Tier, number | null>;
  margin_cents: Record<Tier, number | null>;
  margin_pct: Record<Tier, number | null>;
  min_margin_cents: number | null;
  below_cost: boolean;
  below_floor: boolean;
  implied_price_cents: number | null;
  /** SR-B2: present for services priced by track (Lit Up). */
  variant?: string;
  track?: TrackDetail;
}

/** SR-B2: how a track's economics were computed (ops_variant_economics). */
export interface TrackDetail {
  install_hours: number | null;
  takedown_hours: number | null;
  crew_size: number;
  ladder_work: boolean;
  ladder_premium_cents_per_hr: number;
  trip_cents: number;
  visits: number;
  install_pay_cents: Record<Tier, number | null>;
  takedown_pay_cents: Record<Tier, number | null>;
  alone_margin_cents: Record<Tier, number | null>;
  alone_margin_pct: Record<Tier, number | null>;
  note: string;
}

/** SR-B2: one track (new / own lights) of a service priced by track. */
export interface ServiceVariant {
  variant: string;
  base_price_cents: number;
  kit_cost_cents: number;
  install_hours: number | null;
  takedown_hours: number | null;
  crew_size: number;
  ladder_work: boolean;
  footage_ft: number | null;
  max_qty: number;
  exclusive_group: string | null;
  prices: { zip: string; price_cents: number | null }[];
  economics: Economics | { error: string };
}

export interface CostProfile {
  kit_cost_cents: number;
  disposal_allowance_cents: number;
  equipment_allowance_cents: number;
  other_direct_cost_cents: number;
  mobilization_cents: number | null;
  source: string | null;
  updated_at: string | null;
  updated_by_email: string | null;
}

export interface ServicePrice {
  zip: string;
  price_cents: number | null;
  available_today?: boolean;
  economics?: Economics | { error: string };
}

/** HSC-01: why a service may be booked below the single-booking minimum. */
export type MinVisitExempt = "digital" | "route_delivery" | "quote_visit";
export const MIN_VISIT_EXEMPT_LABEL: Record<MinVisitExempt, string> = {
  digital: "Digital (no visit)",
  route_delivery: "Route delivery (drops along a route)",
  quote_visit: "Quotation visit (mapping, estimate)",
};

export interface Service {
  id: string;
  name: string;
  name_es: string | null;
  category: string | null;
  active: boolean;
  available_months: number[] | null;
  presale_months: number[] | null;
  inactive_regions: Region[] | null;
  base_price_cents: number | null;
  obra_rate_cents: number | null;
  estimated_hours: number | null;
  seasonal_pricing: boolean;
  /** HSC-01: allowed below the single-booking minimum (null = minimum applies). */
  min_visit_exempt?: MinVisitExempt | null;
  /** GM-TXT-02 B3: worker-facing scope line ("Incluye: …"). A line starting with "_" is an unverified Claude draft. */
  scope_es?: string | null;
  scope_en?: string | null;
  scope_updated_at?: string | null;
  scope_updated_by?: string | null;
  cost_profile: CostProfile | null;
  prices: ServicePrice[];
  /** SR-B2: tracks for services priced by track (Lit Up); null otherwise. */
  variants?: ServiceVariant[] | null;
}

export interface ServiceList {
  settings: Settings;
  as_of: string;
  services: Service[];
}

export interface SeasonGrid {
  grid: { region: Region; month: number; multiplier: number }[] | null;
  exempt_services: { id: string; name: string }[] | null;
}

export interface ZipFactor {
  zip: string;
  service_id: string;
  service_name: string | null;
  zip_price_factor: number;
}

export interface LogRow {
  id: number;
  changed_at: string;
  changed_by_email: string | null;
  policy: string;
  target: string;
  old_value: Record<string, unknown> | null;
  new_value: Record<string, unknown> | null;
  reason: string;
  ack_below_cost: boolean;
}

export interface PricedCell {
  price_cents: number;
  available: boolean;
  margin_pct: number | null;
  min_margin_cents: number | null;
  below_cost: boolean;
  below_floor: boolean;
  estimated: boolean;
}
export type Cell = PricedCell | { error: string; available: false };

/** True when a preview cell has a price (not an engine error). */
export const isPriced = (c: Cell | null | undefined): c is PricedCell => !!c && !("error" in c);

export interface PreviewCell {
  service_id: string;
  zip: string;
  month: number;
  before: Cell | null;
  after: Cell | null;
}

export interface Preview {
  change: Change;
  policy: string;
  target: string;
  old: Record<string, unknown> | null;
  new: Record<string, unknown> | null;
  summary: {
    cells: number;
    services_changed: number;
    lowest_margin_pct: number | null;
    below_cost_count: number;
    below_floor_count: number;
    unavailable_cells: number;
  };
  cells: PreviewCell[];
  /** GE-03 A6: active-campaign price locks this change would stop from applying (warn, don't block). */
  campaign_lock_warnings?: LockWarning[];
  applied?: boolean;
  log_id?: number;
}

// GM-PRICE-02: one service's economics by stories
export interface StoryLevel {
  stories: 1 | 2 | 3; price_cents: number | null; ladder_add_cents: number | null;
  by_tier: Partial<Record<Tier, { tarifa: number | null; margin_pct: number | null }>>;
}
export interface StoryEconomics {
  service_id: string; zip: string; floor_pct: number; rate_2_cents_per_hr: number; rate_3_cents_per_hr: number;
  tracks: { variant: string | null; story_step_cents: number | null; levels: StoryLevel[] }[];
}

// GM-TXT-03 D3: Property panel
export type StoriesSource = "assessor" | "owner" | "ops" | "worker_report";
export interface PropertyLookup {
  found: boolean; reason?: string; match?: string; shown_as?: number | null;
  property?: { id: string; gm_code: string; address_normalized: string | null; zip5: string; parcel_apn: string | null; lot_sqft: number | null;
    home_sqft: number | null; year_built: number | null; stories: number | null; stories_source: StoriesSource | null;
    stories_updated_at: string | null; stories_updated_by: string | null };
  county?: { stories: number | null; shown_as: number | null; bldg_type: string | null; file_year: number; ambiguous: boolean } | null;
  open_reports?: string[];
}
export interface StoriesReport {
  id: string; created_at: string; stories: number; message: string | null; maestro: string; job_id: string | null; service: string | null;
  address: string | null; property_id: string | null; current: number | null; current_source: StoriesSource | null; shown_as: number | null;
}
export interface StoriesDisagreement {
  job_id: string; scheduled_date: string | null; service: string; payment_status: string; owner_says: number; county_says: number;
  property_id: string | null; address: string | null;
}
export const STORIES_SOURCE_LABEL: Record<StoriesSource, string> = {
  assessor: "County records", owner: "Property Owner", ops: "Ops", worker_report: "Maestro report (accepted)",
};

// Change payloads — keys match ops_validate_change exactly. A key left out is left unchanged.
export type Change =
  | { type: "service_price"; service_id: string; base_price_cents?: number; obra_rate_cents?: number; estimated_hours?: number }
  | { type: "seasonal_exemption"; service_id: string; seasonal_pricing: boolean }
  | { type: "service_active"; service_id: string; active: boolean }
  | { type: "service_min_exempt"; service_id: string; min_visit_exempt: MinVisitExempt | null }   // HSC-01
  | { type: "service_scope"; service_id: string; scope_es?: string | null; scope_en?: string | null }   // GM-TXT-02 B3
  | { type: "property_fact"; property_id: string; stories: number; report_id?: string }   // GM-TXT-03 D3: house stories
  | {
      type: "service_availability";
      service_id: string;
      available_months?: number[] | null;
      inactive_regions?: Region[];
      presale_months?: number[] | null;
    }
  | { type: "season_multiplier"; region: Region; month: number; multiplier: number }
  | { type: "zip_factor"; zip: string; service_id: string; zip_price_factor: number }
  | {
      type: "cost_profile";
      service_id: string;
      kit_cost_cents?: number;
      disposal_allowance_cents?: number;
      equipment_allowance_cents?: number;
      other_direct_cost_cents?: number;
      mobilization_cents?: number | null;
      source?: string;
    }
  | {
      type: "variant_item";
      service_id: string;
      variant: string;
      base_price_cents?: number;
      kit_cost_cents?: number;
      install_hours?: number;
      takedown_hours?: number;
      crew_size?: number;
      story_step_cents?: number;   // GM-PRICE-02: price step per story above 1 (house lights)
    }
  | ({ type: "settings" } & Partial<
      Pick<
        Settings,
        | "card_fee_pct"
        | "card_fee_fixed_cents"
        | "reference_zips"
        | "target_margin_pct"
        | "margin_floor_pct"
        | "rate_titulo_aprendiz_cents"
        | "rate_titulo_oficial_cents"
        | "rate_titulo_maestro_cents"
        | "mobilization_cents"
        | "price_endings"
        | "ladder_premium_cents_per_hr"
        | "ladder_premium_3_cents_per_hr"
        | "cart_visit_minimum_cents"
        | "single_visit_minimum_enabled"
        | "single_visit_minimum_cents"
      >
    >);

/** An ops_* error, with the text the operator should see. */
export class OpsError extends Error {
  code: string | undefined;
  constructor(raw: { message?: string; code?: string }) {
    super(opsMessage(raw));
    this.code = raw.code;
  }
}

const PREFIXES = ["invalid_change: ", "reason_required: ", "below_cost_requires_ack: ", "ack_required: "];

/** Server messages are shown verbatim after their machine prefix. */
export function opsMessage(raw: { message?: string; code?: string }): string {
  if (raw.code === "42501" || raw.message === "not_authorized") {
    return "Your account isn't allowed to do this.";
  }
  let m = (raw.message || "Something went wrong. Try again.").trim();
  for (const p of PREFIXES) {
    if (m.startsWith(p)) {
      m = m.slice(p.length);
      break;
    }
  }
  return m.charAt(0).toUpperCase() + m.slice(1);
}

async function call<T>(fn: string, args?: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.rpc(fn, args ?? {});
  if (error) throw new OpsError(error);
  return data as T;
}

// ── OPS-FINAL phase 1: Today, Dispatch, Territories, Campaigns, Change log ──

export type QueueKind = "job_unassigned" | "lead_waiting" | "refusals_today" | "job_no_photos" | "counter_needs_approval";
export interface QueueItem {
  kind: QueueKind;
  target_id: string;
  title: string;
  zip: string | null;
  waiting_since: string;
  action: string;
  count: number | null;
  can_text: boolean;
}
export interface TodayData {
  header: { jobs_needing_maestro: number; leads_waiting_4h: number; active_jobs: number; counters_need_approval?: number };
  items: QueueItem[];
}

export interface LeadDetail {
  id: string;
  name: string | null;
  address: string | null;
  contact: string | null;
  budget: string | null;
  intents: string[] | null;
  source: string | null;
  created_at: string;
  contacted_at: string | null;
  contact_channel: string | null;
  sms_consent: boolean;
}
export type ContactChannel = "phone" | "email" | "text" | "in_person";

export interface JobRow {
  id: string;
  short_ref: string;
  service_id: string | null;
  service_name: string | null;
  zip: string | null;
  scheduled_date: string | null;
  created_at: string;
  status: string;
  payment_status: string;
  cascade_step: number | null;
  maestro_id: string | null;
  maestro_name: string | null;
  price_cents: number | null;
  waiting_since: string;
  has_photos: boolean;
}
export interface JobList {
  rows: JobRow[];
  total: number;
  next_cursor: number | null;
  proof_of_work: { completed: number; with_photos: number };
}
export interface TimelineEvent {
  event: string;
  at: string | null;
  maestro: string | null;
  rate_cents: number | null;
  notes: string | null;
}
export interface JobDetail {
  job: {
    id: string;
    short_ref: string;
    service_id: string | null;
    service_name: string | null;
    status: string;
    payment_status: string;
    cascade_step: number | null;
    escalation_round: number | null;
    dispatch_mode: string | null;
    zip: string | null;
    scheduled_date: string | null;
    scheduled_time_start: string | null;
    scheduled_time_end: string | null;
    step_started_at: string | null;
    maestro_rate_cents: number | null;
    price_cents: number | null;
    /** SP-POST-09a: counters ≤ auto cap are approved automatically (25% margin); ≤ ceiling need the owner (18% floor) */
    auto_cap_cents?: number | null;
    ceiling_cents?: number | null;
  };
  maestro: { id: string; name: string | null; titulo: string | null; has_phone: boolean; language: string } | null;
  homeowner: { name: string | null; email: string | null; phone: string | null } | null;
  property: { address: string | null; city: string | null; zip: string | null } | null;
  timeline: TimelineEvent[];
  paid: boolean;
  open_offers: { maestro: string | null; level: string | null; expires_at: string | null }[];
  photos: { path: string; at: string }[];
  /** PAY-01: pending counter-offers ("Mi Precio"), margin computed in the database */
  counters?: CounterOffer[];
  /** PAY-01: the payout recorded when the job was completed */
  payout?: JobPayout;
}
export interface CounterOffer {
  counter_id: string;
  contractor_id: string;
  maestro: string | null;
  titulo: string | null;
  offered_rate_cents: number;
  created_at: string;
  margin_cents: number | null;
  margin_pct: number | null;
  price_cents: number | null;
  /** SP-POST-09a (ops_job_detail): auto = within 25% margin · approval = 25–18%, owner decides ·
   *  above_floor = past the 18% floor, can't be accepted · no_caps = legacy/multi-service job, owner's call */
  band?: "auto" | "approval" | "above_floor" | "no_caps";
}
export type PayoutStatus = "pending" | "scheduled" | "needs_review" | "held" | "processing" | "paid" | "failed" | "sent" | "skipped" | "no_connect_account";
export interface JobPayout {
  status: PayoutStatus | null;
  payout_cents: number | null;
  suggested_cents: number | null;
  basis: string | null;
  review_reason: string | null;
  tier: string | null;
  margin_cents: number | null;
  margin_pct: number | null;
  computed_at: string | null;
  eligible_at: string | null;
  paid_at: string | null;
}
export interface Candidate {
  id: string;
  name: string | null;
  titulo: string | null;
  jobs_completed: number;
  auto_accept: boolean;
  has_phone: boolean;
  language: string;
  blackout: boolean;
}
export interface RestartPlan {
  action: string;
  restart_at_step: 2 | 3;
  rate_cents: number;
  texts: string;
  maestros: { name: string | null; language: string; has_phone: boolean }[];
}

export type LifecycleStatus = "prospect" | "testing" | "building" | "launched" | "ramping" | "proven" | "paused";
export interface TerritoryRow {
  zip: string;
  label: string | null;
  is_active: boolean;
  lifecycle_status: LifecycleStatus | null;
  decision: string | null;
  wave: number | null;
  launch_date: string | null;
  view: "launched" | "prospect";
  maestros_serving: number;
  paid_jobs_30d: number;
  median_hours_to_assign: number | null;
  scans: number;
  scan_rate_pct: number | null;
  total_score: number | null;
  owner_occupied_pct: number | null;
  median_hh_income: number | null;
  median_home_value: number | null;
  population: number | null;
  mail_homes: number | null;
}
export interface ZipDetail {
  zip: string;
  lifecycle: Record<string, unknown> | null;
  census: Record<string, number | string | boolean | null> | null;
  language: Record<string, number | string | null> | null;
  mail_summary: Record<string, number | string | null> | null;
  routes: {
    carrier_route: string;
    route_type: string | null;
    residences: number | null;
    businesses: number | null;
    median_income: number | null;
    median_age: number | null;
    avg_hh_size: number | null;
  }[];
  campaigns: { id: string; name: string; status: string | null; start_date: string | null; end_date: string | null }[];
  maestros: { id: string; name: string | null; titulo: string | null; auto_accept: boolean }[];
}
/** Lot-size mix per EDDM carrier route (ops_route_size_mix, 00025 v8 D3). Homes = county-assessor houses, no apartments/condos. */
export interface RouteSizeRow {
  zip_code: string;
  carrier_route: string;
  homes: number;
  under_max: number;
  pct_under_max: number;
  pct_custom: number;
  median_lot: number | null;
  usps_res_count: number | null;
  p95_dist_m: number | null;
  priceable: boolean;
}
export interface RouteSizeMix {
  zip: string;
  max_lot_sqft: number;
  priceable_pct: number;
  summary: {
    routes: number;
    homes: number;
    homes_under_max: number;
    priceable_routes: number;
    homes_on_priceable_routes: number;
    usps_res_on_priceable_routes: number;
  };
  routes: RouteSizeRow[];
}
/** ZIP activation chain (zip-onboard, 00025 v8 D3). One row per ZIP in zip_onboarding. */
export type OnboardingStatus = "queued" | "running" | "awaiting_confirm" | "confirmed" | "done" | "failed" | "no_parcel_source";
export type OnboardingStepKey = "census" | "eddm" | "parcels_dry" | "parcels" | "routes" | "monitor9";
export interface OnboardingStep {
  status: "running" | "ok" | "failed" | "skipped";
  at: string;
  result?: Record<string, unknown> | null;
}
export interface ZipOnboarding {
  zip: string;
  county: string | null;
  status: OnboardingStatus;
  steps: Partial<Record<OnboardingStepKey, OnboardingStep>> & { note?: string };
  error: string | null;
  started_at: string;
  updated_at: string;
  confirmed_by: string | null;
  confirmed_at: string | null;
}
export interface ZipUpsert {
  zip: string;
  label?: string;
  status?: LifecycleStatus;
  decision?: "proven" | "pivot" | "pending";
  wave?: number;
  launch_date?: string;
  notes?: string;
}

export type FunnelStage = "engage" | "scan" | "price_view" | "checkout_start" | "submit" | "pay";
export interface CampaignResult {
  id: string;
  name: string;
  slug: string | null;
  zip: string | null;
  start_date: string | null;
  end_date: string | null;
  status: string | null;
  mailer_count: number | null;
  scans: number;
  conversions: number;
  paid_revenue_cents: number;
  funnel: Record<FunnelStage, number>;
  unattributed: boolean;
  // GE-03 list fields (null on the Unattributed row)
  purpose?: Purpose | null;
  channel?: Channel | null;
  flyer_id?: string | null;
  slogan?: string | null;
  lock_count?: number | null;
  approved_at?: string | null;
}
export interface CampaignDetail {
  campaign: {
    id: string;
    name: string;
    zip: string | null;
    start_date: string | null;
    end_date: string | null;
    status: string | null;
    mailer_count: number | null;
  };
  funnel: { stage: FunnelStage; count: number; drop_off_pct: number | null }[];
  daily_scans: { day: string; scans: number }[];
  routes: { carrier_route: string; residences: number | null; scans: number | null; scan_rate_pct: number | null }[];
  routes_tracked: boolean;
  variants: { variant: string; scans: number; conversions: number; conversion_pct: number | null }[];
  variant_winner: string | null;
  variant_min_scans: number;
}
export interface Refusal {
  service_id: string | null;
  service_name: string | null;
  error_code: string;
  count: number;
  last_at: string;
}
export type LogSection = "pricing" | "dispatch" | "leads" | "territories" | "photos" | "campaigns" | "money";
export interface ActivityRow extends LogRow {
  section: LogSection;
  preview_summary?: Record<string, unknown> | null;
}

/** Calls an edge function with the operator's own sign-in; server messages are shown verbatim. */
async function invoke<T>(fn: string, body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke(fn, { body });
  if (error) {
    let payload: { message_en?: string; error?: string } | null = null;
    try {
      const ctx = (error as { context?: Response }).context;
      if (ctx && typeof ctx.json === "function") payload = await ctx.json();
    } catch {
      payload = null;
    }
    throw new OpsError({ message: fnMessage(payload) ?? error.message });
  }
  const d = data as { error?: string; message_en?: string } | null;
  if (d && typeof d === "object" && d.error) throw new OpsError({ message: fnMessage(d) ?? d.error });
  return data as T;
}

function fnMessage(p: { message_en?: string; error?: string } | null): string | null {
  if (!p) return null;
  if (p.message_en) return p.message_en;
  if (p.error === "not_authorized") return "Your account isn't allowed to do this.";
  if (p.error === "sign_in_required") return "Your sign-in has expired. Sign in again.";
  return p.error ? p.error.replace(/_/g, " ") : null;
}

// ── SP-POST-09a: Maestro readiness (ops_maestro_readiness) ──
export interface MaestroReadiness {
  id: string;
  name: string;
  business_name: string | null;
  titulo: string | null;
  email: string | null;
  joined: string;
  phone: string | null;
  phone_ok: boolean;
  sms_consent: boolean;
  compact: boolean;
  active_zones: number;
  zips: string[];
  stripe: boolean;
  missing: string[];
  ready: boolean;
  is_test?: boolean;
  // AD-25b T6: license
  license_number?: string | null;
  license_verified?: boolean;
  license_verified_at?: string | null;
  license_verified_by?: string | null;
}
export interface ContractorTitulo { oficio: string; titulo: "aprendiz" | "oficial" | "maestro"; visibility: string | null; carta: string | null; holder: "self" | "crew" }

export interface MaestroReadinessData {
  maestros: MaestroReadiness[];
  ready: number;
  test_accounts_hidden: number;
  test_accounts_total?: number;
  texts_live: boolean;
  license_gate?: boolean;   // AD-25b T6: job offers wait for a verified license
}

// ── PROC-01 M6: supplier orders waiting on the owner ──
export interface ProcQueueItem {
  order_id: string; po_number: string; status: "awaiting_approval" | "needs_review"; status_reason: string | null;
  test_mode: boolean; fulfillment: "delivery" | "will_call"; supplier: string; location: string | null;
  deliver_on: string | null; pickup_on: string | null; service_catalog_id: string | null; gm_code: string | null; waiting_since: string;
  supplier_doc_no: string | null; quote_total: number | null; quote_tax: number | null; hold_expires_at: string | null;
  blocking: number; flags: number;
  // IDC-01 (record 155): print/mail orders have no job or property
  order_kind?: "materials" | "mail"; mail_kind?: string | null; mail_campaign_id?: string | null; mail_pieces?: number | null;
}
export interface ProcCheck { id: string; ok: boolean; severity: "block" | "flag" | "info"; detail: string }
export interface ProcLine { sku: string | null; description: string | null; unit: string | null; qty: number | null; unit_price: number | null; net: number | null; kind: string }
export interface ProcOrder {
  order_id: string; po_number: string; status: string; status_reason: string | null; test_mode: boolean;
  buyer_name: string; account_no: string | null; supplier: string;
  location: { name: string; address: string; city: string; phone: string | null } | null;
  fulfillment: "delivery" | "will_call"; ship_to: Record<string, unknown> | null;
  deliver_on: string | null; delivery_latest?: string | null; delivery_window: string | null; delivery_instructions: string | null;
  confirmed_delivery_on?: string | null; confirmed_delivery_window?: string | null;
  pickup_on: string | null; pickup_window: string | null; pickup_contact: string | null;
  requested_lines: Array<{ sku: string; qty: number; description: string }>; est_total_cents: number | null;
  approved_by: string | null; approved_at: string | null; service_catalog_id: string | null; gm_code: string | null; tax_exempt_today: boolean;
  // IDC-01 (record 155): print/mail orders
  order_kind?: "materials" | "mail";
  mail?: null | { mail_order_id: number; kind: string; campaign_id: string | null; zip: string | null; route: string | null;
    pieces: number; mail_by: string | null; est_cost_cents: number | null; vecinos_request_id: number | null };
  quote: null | { document_id: string; supplier_doc_no: string | null; account_no: string | null; buyer_name_seen: string | null;
    totals: { subtotal: number | null; charges: number | null; tax: number | null; total: number | null } | null;
    hold_expires_at: string | null; doc_date: string | null; shipping_method: string | null;
    checks: { results?: ProcCheck[]; match_method?: string } | null; has_pdf: boolean; from_addr: string | null; received_at: string; lines: ProcLine[] };
  history: Array<{ direction: "in" | "out"; doc_type: string; status: string; subject: string | null; at: string }>;
}
export interface ProcActResult { ok: boolean; status?: string; sent_to?: string; test_mode?: boolean; url?: string | null }

// ── IDC-01: "Mail it" — print/mail orders through the supplier-order flow (records 150, 155, 157) ──
export interface PrintVendor { supplier_id: string; name: string; test_mode: boolean; order_email: string | null }
export interface MailItResult { ok: boolean; order_id: string; po_number: string; mail_order_id: number; vendor: string;
  pieces: number; est_cost_cents: number | null; test_mode: boolean; reason?: string; message?: string }

// ── PROC-02: Admin → Orders ──
export interface ProcJobRow {
  job_id: string; service: string; job_status: string; payment_status: string | null; scheduled_date: string | null; zip: string | null;
  gm_code: string | null; address: string | null; homeowner_email: string | null; pending_lines: number; open_orders: number; created_at: string;
}
export interface ProcLocation { id: string; name: string; address: string; city: string; delivery: boolean; will_call: boolean; hours: string | null }
export interface ProcSupplier {
  supplier_id: string; name: string; test_mode: boolean; tax_status: string; default_location_id: string | null;
  default_delivery_instructions: string | null; locations: ProcLocation[];
}
export interface ProcJobLine { id: string; supplier_id: string | null; sku: string | null; name: string | null; color: string | null; qty: number; unit: string | null; catalog_price: number | null; status: string; order_ref: string | null }
export interface ProcJobOrder {
  order_id: string; po_number: string; status: string; fulfillment: string; supplier: string; location: string | null;
  deliver_on: string | null; delivery_latest: string | null; pickup_on: string | null; tax_treatment: string; test_mode: boolean; created_at: string;
}
export interface ProcJob {
  job_id: string; service: string; service_catalog_id: string | null; job_status: string; payment_status: string | null; scheduled_date: string | null;
  zip: string | null; internal: boolean; property: { id: string; gm_code: string; address: string } | null;
  link_hint: { gm_code: string; address: string } | null; lines: ProcJobLine[]; orders: ProcJobOrder[]; suppliers: ProcSupplier[];
}
export interface CatalogHit { id: string; sku: string; name: string; color: string | null; product_line: string | null; unit: string; price: number | null; dimensions: string | null; active_for_customers: boolean }
export interface BuildOrderInput {
  job_id: string; supplier_id: string; location_id?: string; fulfillment: "delivery" | "will_call";
  deliver_on?: string; delivery_latest?: string; delivery_window?: string; delivery_instructions?: string;
  pickup_on?: string; pickup_window?: string; pickup_contact?: string; internal_purchase?: boolean;
}
export interface BuildOrderResult { ok: boolean; order_id: string; po_number: string; warnings?: string[]; location?: string; fulfillment?: string; estimate?: Record<string, unknown> }
export interface SendRfqResult { ok: boolean; po_number: string; sent_to: string; test_mode: boolean }

/** PROC-02: the order functions answer { ok:false, reason, message } — turn that (and non-2xx) into a readable error. */
async function procInvoke<T>(fn: string, body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke(fn, { body });
  const say = (p: { message?: string; message_en?: string; reason?: string; error?: string } | null) =>
    p?.message ?? p?.message_en ?? (p?.reason ?? p?.error ?? "").replace(/_/g, " ");
  if (error) {
    let payload: Record<string, string> | null = null;
    try { const ctx = (error as { context?: Response }).context; if (ctx && typeof ctx.json === "function") payload = await ctx.json(); } catch { payload = null; }
    throw new OpsError({ message: say(payload) || error.message });
  }
  const d = data as { ok?: boolean; problems?: Array<{ item?: string; reason?: string }>; message?: string; message_en?: string; reason?: string; error?: string } | null;
  if (d && d.ok === false) {
    const extra = (d.problems ?? []).map((p) => `${p.item ?? "line"}: ${(p.reason ?? "").replace(/_/g, " ")}`).join("; ");
    throw new OpsError({ message: [say(d), extra].filter(Boolean).join(" — ") });
  }
  return data as T;
}

// ── OPS-METRICS-01: route flywheel board (lit routes) ─────────────────────
export type RouteState = "cold" | "warming" | "lit" | "cooling";
export type TestResult = "pass" | "fail" | "no_data";
export type LitTest = "volume" | "pays_its_mail" | "organic" | "liquidity" | "season_loop";
export interface RouteBoardRow {
  zip: string;
  route: string;
  state: RouteState;
  homes: number | null;
  jobs: number;
  sources: { flyer: number; repeat: number; starred: number; direct: number };
  contribution_cents: number;
  marketing_cents: number;
  /** contribution ÷ marketing; null when nothing was mailed. */
  mail_multiple: number | null;
  /** 0–1 fractions (not percentages). */
  organic_share: number | null;
  median_accept_min: number | null;
  fill_by_step2: number | null;
  active_maestros: number;
  second_service_share: number | null;
  tests: Record<LitTest, TestResult>;
  tests_passed: number;
  /** Plain-English gaps, most important first. */
  missing: string[];
}
export interface FlywheelSettings {
  lit_min_jobs_month: number;
  lit_min_mail_multiple: number;
  lit_min_organic_share: number;
  lit_max_median_accept_min: number;
  lit_min_fill_by_step2: number;
  lit_min_active_maestros: number;
  lit_min_second_service: number;
  lit_months_required: number;
  min_sample_jobs: number;
  min_cohort_customers: number;
  active_maestro_days: number;
  maestro_radius_km: number;
  breakeven_fixed_cents_month: number;
  next_zip_launch_cents: number;
}
export interface RouteBoard {
  month: string;
  computed_at: string | null;
  thresholds: FlywheelSettings;
  totals: {
    routes: number; lit: number; warming: number; cooling: number; jobs: number; unassigned_jobs: number;
    surplus_cents: number; breakeven_pct: number | null; trailing_90d_surplus_cents: number;
    next_zip_pct: number | null; campaigns_missing_cost: number;
  };
  zips: { zip_code: string; routes: number; lit: number; warming: number; cooling: number; cold: number; jobs: number;
          contribution_cents: number; marketing_cents: number; surplus_cents: number; unassigned_jobs: number }[];
  routes: RouteBoardRow[];
}
export type FlywheelItemKind = "route_cooling" | "route_lit" | "campaign_cost_missing" | "jobs_unassigned";
export interface FlywheelItem {
  kind: FlywheelItemKind;
  priority: number;
  title: string;
  detail: string;
  zip?: string;
  route?: string;
  campaign_id?: string;
  count?: number;
}

// ── SUPPLY (Oct 29, 2026, record 100): local supply switches — one category, one market, turned on by hand ──
export type SupplyCategory = "hardscape" | "bulk" | "bigbox" | "nursery" | "irrigation";
export const SUPPLY_CATEGORIES: { key: SupplyCategory; label: string }[] = [
  { key: "hardscape", label: "Hardscape (pavers, wall block, caps)" },
  { key: "bulk", label: "Bulk (rock, soil, mulch)" },
  { key: "bigbox", label: "Big box (Lowe's / Home Depot items)" },
  { key: "nursery", label: "Nursery (plants, trees)" },
  { key: "irrigation", label: "Irrigation" },
];
export interface SupplyRow { zip: string; category: SupplyCategory; supplier_id: string | null; supplier_name: string | null; active: boolean; reason: string | null; by: string | null; at: string | null }
export interface SupplySupplier { id: string; name: string; type: SupplyCategory; orderable: boolean }
export interface SupplyList { rows: SupplyRow[]; suppliers: SupplySupplier[] }
export interface SupplyOrder {
  quote_id: string; quote_number: string; obra_job_id: string; contractor: string | null; zip: string; category: SupplyCategory;
  supplier_name: string | null; paid_at: string; scheduled_date: string | null; est_cents: number; po: string | null;
  rows: { description: string; qty: number | null; unit: string | null; est_cents: number | null; ref: string | null }[];
}

export const ops = {
  listSupply: (zip?: string) => call<SupplyList>("ops_list_supply", { p_zip: zip ?? null }),
  setSupply: (zips: string[], category: SupplyCategory, supplierId: string | null, active: boolean, reason: string) =>
    call<{ ok: boolean; zips: number }>("ops_set_supply", { p_zips: zips, p_category: category, p_supplier_id: supplierId, p_active: active, p_reason: reason }),
  supplyOrders: () => call<SupplyOrder[]>("ops_supply_orders"),
  whoami: () => call<WhoAmI>("ops_whoami"),
  listServices: () => call<ServiceList>("ops_list_services"),
  listSeasonMultipliers: () => call<SeasonGrid>("ops_list_season_multipliers"),
  listZipFactors: (zip?: string) => call<ZipFactor[]>("ops_list_zip_factors", { p_zip: zip ?? null }),
  getSettings: () => call<Settings>("ops_get_settings"),
  quotesOverview: (status?: string, source?: string) => call<QuotesOverview>("ops_quotes_overview", { p_status: status ?? null, p_source: source ?? null }),   // #31
  quoteDetail: (quoteNumber: string) => call<QuoteDetail>("ops_quote_detail", { p_quote_number: quoteNumber }),
  plansOverview: () => call<PlansOverview>("ops_plans_overview"),
  refundsOverview: () => call<RefundsOverview>("ops_refunds_overview"),
  refundAction: (id: string, action: "retry" | "mark_done", note: string) => call<{ ok: boolean; reason?: string }>("ops_refund_action", { p_id: id, p_action: action, p_note: note }),
  cancelPlan: (planId: string, note: string) => call<{ ok: boolean; reason?: string; visits_cancelled?: number }>("ops_cancel_plan", { p_plan_id: planId, p_note: note }),
  catalogIntegrity: () => call<IntegrityReport>("ops_catalog_integrity"),   // 00040 §6
  catalogExport: () => call<CatalogExport>("ops_catalog_export"),           // 00040 §7
  changeLog: (limit = 200, target?: string) =>
    call<LogRow[]>("ops_change_log", { p_limit: limit, p_target: target || null }),
  preview: (change: Change) => call<Preview>("ops_preview_change", { p_change: change }),
  apply: (change: Change, reason: string, ackBelowCost: boolean) =>
    call<Preview>("ops_apply_change", { p_change: change, p_reason: reason, p_ack_below_cost: ackBelowCost }),

  // GM-PRICE-02: price, tarifa and margin at 1 / 2 / 3 stories (null when the service doesn't ask stories)
  storyEconomics: (serviceId: string) => call<StoryEconomics | null>("ops_story_economics", { p_service_id: serviceId }),
  // GM-TXT-03 D3: Property panel (house stories)
  propertyLookup: (street: string, zip: string) => call<PropertyLookup>("ops_property_lookup", { p_street: street, p_zip: zip }),
  storiesReports: () => call<StoriesReport[]>("ops_stories_reports"),
  rejectStoriesReport: (id: string, reason: string) => call<{ ok: boolean; reason?: string }>("ops_reject_stories_report", { p_id: id, p_reason: reason }),
  storiesDisagreements: () => call<StoriesDisagreement[]>("ops_stories_disagreements"),

  // Today
  today: () => call<TodayData>("ops_today"),
  procurementQueue: () => call<ProcQueueItem[]>("ops_procurement_queue"),
  procurementJobs: (q?: string) => call<ProcJobRow[]>("ops_procurement_jobs", { p_q: q ?? null }),
  procurementJob: (jobId: string) => call<ProcJob>("ops_procurement_job", { p_job_id: jobId }),
  catalogSearch: (supplierId: string, q: string) => call<CatalogHit[]>("ops_catalog_search", { p_supplier_id: supplierId, p_q: q }),
  bomAddLine: (jobId: string, itemId: string, qty: number) => call<{ ok: boolean; message?: string }>("ops_bom_add_line", { p_job_id: jobId, p_catalog_item_id: itemId, p_qty: qty }),
  bomRemoveLine: (lineId: string) => call<{ ok: boolean; message?: string }>("ops_bom_remove_line", { p_line_id: lineId }),
  linkJobProperty: (jobId: string) => call<{ ok: boolean; gm_code?: string; message?: string; reason?: string }>("link_job_property", { p_job_id: jobId }),
  buildOrder: (input: BuildOrderInput) => procInvoke<BuildOrderResult>("build-procurement-order", input as unknown as Record<string, unknown>),
  sendRfq: (orderId: string) => procInvoke<SendRfqResult>("send-procurement-rfq", { order_id: orderId }),
  procurementOrder: (orderId: string) => call<ProcOrder>("ops_procurement_order", { p_order_id: orderId }),
  // IDC-01: print/mail vendors + "Mail it" (builds a GM-MARKET-PO draft; the caller then sends the quote request with sendRfq)
  printVendors: () => call<PrintVendor[]>("ops_print_vendors"),
  mailIt: async (source: "vecinos" | "campaign", ref: string, supplierId: string, instructions?: string) => {
    const r = await call<MailItResult>("ops_mail_it", { p_source: source, p_ref: ref, p_supplier_id: supplierId, p_test: false, p_instructions: instructions ?? null });
    if (!r?.ok) throw new OpsError({ message: r?.message ?? String(r?.reason ?? "mail_it_failed").replace(/_/g, " ") });
    return r;
  },
  procurementAct: (orderId: string, action: "approve" | "reject" | "request_changes" | "pdf_url", note?: string, acknowledgeFlags?: boolean) =>
    invoke<ProcActResult>("approve-procurement-order", { order_id: orderId, action, note: note ?? null, acknowledge_flags: acknowledgeFlags ?? false }),
  maestroReadiness: (includeTest = false) => call<MaestroReadinessData>("ops_maestro_readiness", { p_include_test: includeTest }),
  // Profile v1: títulos GM may use for ops, per contractor (GM cache only; counts live in MDO → Revisiones)
  contractorTitulos: (ids: string[]) => call<Record<string, ContractorTitulo[]>>("ops_contractor_titulos", { p_ids: ids }),
  // AD-25b T6: mark a Maestro's license verified / not verified (pricing_admin+, logged as dispatch_license_verified)
  setLicenseVerified: (contractorId: string, verified: boolean, reason: string) =>
    call<{ contractor_id: string; verified: boolean; log_id: number }>("ops_set_license_verified", { p_contractor_id: contractorId, p_verified: verified, p_reason: reason }),
  leadDetail: (leadId: string) => call<LeadDetail>("ops_lead_detail", { p_lead_id: leadId }),
  markLeadContacted: (leadId: string, channel: ContactChannel, note: string) =>
    call<{ id: string; contacted_at: string }>("ops_mark_lead_contacted", { p_lead_id: leadId, p_channel: channel, p_note: note || null }),
  refusals: (from?: string, to?: string) => call<Refusal[]>("ops_refusals", { p_from: from ?? null, p_to: to ?? null }),

  // Dispatch (reads)
  listJobs: (view: "open" | "completed", cursor = 0) => call<JobList>("ops_list_jobs", { p: { view, limit: 100, cursor } }),
  jobDetail: (jobId: string) => call<JobDetail>("ops_job_detail", { p_job_id: jobId }),
  jobCandidates: (jobId: string) => call<Candidate[]>("ops_job_candidates", { p_job_id: jobId }),
  photoUrl: async (path: string): Promise<string | null> => {
    const { data } = await supabase.storage.from("job-photos").createSignedUrl(path, 600);
    return data?.signedUrl ?? null;
  },

  // Dispatch (actions — the edge functions check the operator role and write the change log)
  // PAY-01: owner accepts a counter-offer and assigns in one step (obra-accept-job v13)
  acceptCounter: (jobId: string, contractorId: string, counterId: string, reason: string) =>
    invoke<{ action: string; logged: boolean }>("obra-accept-job", {
      job_id: jobId,
      contractor_id: contractorId,
      counter_offer_id: counterId,
      reason,
    }),
  rejectCounter: (counterId: string, reason: string) =>
    call<{ counter_id: string; counter_status: string }>("ops_reject_counter_offer", { p_counter_id: counterId, p_reason: reason }),
  setPayoutHold: (jobId: string, hold: boolean, reason: string) =>
    call<{ job_id: string; payout_status: string }>("ops_set_payout_hold", { p_job_kind: "obra", p_job_id: jobId, p_hold: hold, p_reason: reason }),
  assign: (jobId: string, contractorId: string, reason: string) =>
    invoke<{ action: string; logged: boolean }>("obra-accept-job", { job_id: jobId, contractor_id: contractorId, reason }),
  cancel: (jobId: string, reason: string) =>
    invoke<{ action: string; logged: boolean }>("obra-cancel-job", { job_id: jobId, cancelled_by: "ops", reason }),
  reschedule: (jobId: string, newDate: string, reason: string) =>
    invoke<{ action: string; texted: string[]; logged: boolean }>("obra-reschedule-job", { job_id: jobId, new_date: newDate, reason }),
  restartPreview: (jobId: string) => invoke<RestartPlan>("obra-start-cascade", { job_id: jobId, restart: true, preview: true }),
  restart: (jobId: string, reason: string) =>
    invoke<RestartPlan & { texted_now: number; logged: boolean }>("obra-start-cascade", { job_id: jobId, restart: true, reason }),

  // Territories
  territories: () => call<TerritoryRow[]>("ops_territories"),
  zipDetail: (zip: string) => call<ZipDetail>("ops_zip_detail", { p_zip: zip }),
  zipOnboarding: (zip: string) => call<ZipOnboarding | null>("ops_zip_onboarding", { p_zip: zip }),
  startZipOnboarding: (zip: string) => invoke<{ started: string; by: string }>("zip-onboard", { action: "start", zip }),
  confirmZipOnboarding: (zip: string) => invoke<{ confirmed: string; by: string }>("zip-onboard", { action: "confirm", zip }),
  routeSizeMix: (zip: string, maxLotSqft = 10000, priceablePct = 80) =>
    call<RouteSizeMix>("ops_route_size_mix", { p_zip: zip, p_max_lot_sqft: maxLotSqft, p_priceable_pct: priceablePct }),
  upsertZip: (p: ZipUpsert, reason: string) =>
    call<{ zip: string; added: boolean }>("ops_upsert_zip", { p, p_reason: reason }),
  importZips: (zips: { zip_code: string; label: string }[]) =>
    invoke<{ imported: number; skipped: number; invalid: number }>("import-target-zips", { zips }),
  // OPS-METRICS-01: route flywheel board (nightly snapshot; owner edits the rules)
  routeLitBoard: (zip: string | null, month?: string) =>
    // omit p_month (not null) so the server's default — this month — applies
    call<RouteBoard>("ops_route_lit_board", month ? { p_month: month, p_zip: zip } : { p_zip: zip }),
  flywheelToday: () => call<{ month: string; items: FlywheelItem[] }>("ops_flywheel_today"),
  setFlywheelSettings: (patch: Partial<FlywheelSettings>, reason: string) =>
    call<FlywheelSettings>("ops_set_flywheel_settings", { p: patch, p_reason: reason }),
  refreshCensus: async () => {
    const stats = await invoke<{ zips_fetched: number; zips_missing: string[] }>("refresh-zip-stats", {});
    await invoke<unknown>("refresh-eddm-routes", {});
    return stats;
  },

  // Campaigns → Results
  campaignResults: () => call<CampaignResult[]>("ops_campaign_results", { p_from: null, p_to: null }),
  campaignDetail: (campaignId: string) =>
    call<CampaignDetail>("ops_campaign_detail", { p_campaign_id: campaignId, p_from: null, p_to: null }),

  // GE-03 campaign wizard (every call checks the operator's role on the server)
  campaignOptions: () => call<CampaignOptions>("ops_campaign_options"),
  saveCampaign: (p: CampaignDraftInput) => call<CampaignRow>("ops_save_campaign", { p }),
  previewLock: (p: LockInput) => call<LockPreview>("ops_preview_campaign_lock", { p }),
  setLock: (p: LockInput) => call<LockEval>("ops_set_campaign_lock", { p }),
  removeLock: (p: { campaign_id: string; service_id: string; zip?: string | null }) =>
    call<{ removed: unknown }>("ops_remove_campaign_lock", { p }),
  setRoutes: (campaignId: string, routes: string[]) =>
    call<{ routes: { carrier_route: string; res_count: number }[]; addresses: number }>("ops_set_campaign_routes", {
      p_campaign_id: campaignId,
      p_routes: routes,
    }),
  campaignReview: (campaignId: string, conversionPct = 2) =>
    call<CampaignReview>("ops_campaign_review", { p_campaign_id: campaignId, p_conversion_pct: conversionPct }),
  approveCampaign: (campaignId: string, acks: string[], reason: string) =>
    call<{ id: string; status: string; approved_at: string }>("ops_approve_campaign", {
      p_campaign_id: campaignId,
      p_acks: acks,
      p_reason: reason,
    }),
  // OPS-METRICS-01: print + postage, so routes can be scored and break-even computed
  setCampaignCost: (campaignId: string, costCents: number, reason: string) =>
    call<{ campaign_id: string; name: string; old_cost_cents: number | null; cost_cents: number }>("ops_set_campaign_cost", {
      p_campaign_id: campaignId,
      p_cost_cents: costCents,
      p_reason: reason,
    }),
  endCampaign: (campaignId: string, reason: string) =>
    call<{ id: string; status: string }>("ops_end_campaign", { p_campaign_id: campaignId, p_reason: reason }),
  setDiscountApprovals: (enabled: boolean, reason: string) =>
    call<{ discount_approvals_enabled: boolean }>("ops_set_discount_approvals", { p_enabled: enabled, p_reason: reason }),

  // Change log (every section)
  activityLog: (limit: number, section?: LogSection, target?: string) =>
    call<ActivityRow[]>("ops_activity_log", { p_limit: limit, p_section: section ?? null, p_target: target || null }),
};

export const canApply = (role: Role | null): boolean => role === "owner" || role === "pricing_admin";

export const ROLE_LABEL: Record<Role, string> = {
  owner: "Owner",
  pricing_admin: "Pricing admin",
  viewer: "Viewer",
};

export const REGION_LABEL: Record<Region, string> = { pnw: "PNW", norcal: "NorCal" };

export const POLICY_LABEL: Record<string, string> = {
  service_price: "Price & hours",
  seasonal_exemption: "Seasonality",
  service_active: "On / off",
  service_availability: "Availability",
  season_multiplier: "Seasonal multiplier",
  zip_factor: "ZIP factor",
  cost_profile: "Costs",
  variant_item: "Track (new / own lights)",
  service_scope: "What the worker sees",
  property_fact: "House stories",
  stories_report_rejected: "Stories report rejected",
  settings: "Settings",
  dispatch_assign: "Maestro assigned",
  dispatch_counter_accept: "Counter-offer accepted",
  dispatch_counter_reject: "Counter-offer rejected",
  dispatch_payout_hold: "Payout held",
  dispatch_payout_release: "Payout released",
  dispatch_cancel: "Job cancelled",
  dispatch_reschedule: "Job rescheduled",
  dispatch_restart: "Cascade restarted",
  dispatch_sms: "Text sent",
  lead_contacted: "Lead contacted",
  zip_add: "ZIP added",
  zip_status: "ZIP status",
  campaign_create: "Campaign draft created",
  campaign_update: "Campaign draft edited",
  campaign_lock_set: "Price lock set",
  campaign_lock_remove: "Price lock removed",
  campaign_routes_set: "Mail routes chosen",
  campaign_approve: "Campaign approved",
  campaign_end: "Campaign ended",
  campaign_discount_approvals: "Discount approvals switched",
};

export const LIFECYCLE_LABEL: Record<LifecycleStatus, string> = {
  prospect: "Prospect",
  testing: "Testing",
  building: "Building",
  launched: "Launched",
  ramping: "Ramping",
  proven: "Proven",
  paused: "Paused",
};

export const STATUS_LABEL: Record<string, string> = {
  draft: "Draft",
  cascade_active: "Finding a Maestro",
  preference_pending: "Offered to preferred Maestros",
  auto_assigned: "Auto-assigned",
  review_window: "Maestro reviewing",
  assigned: "Assigned",
  committed: "Confirmed",
  in_progress: "In progress",
  completed: "Completed",
  cancelled: "Cancelled",
  needs_ops: "Needs you",
};

/** The economics for a service at its first reference ZIP, or null when it has no price. */
export function primaryEconomics(s: Service): Economics | null {
  const e = s.prices[0]?.economics;
  return e && !("error" in e) ? e : null;
}

// ── GE-03 campaign wizard ──────────────────────────────────

export type CampaignStatus = "draft" | "active" | "ended" | "archived";
export type Purpose = "service_discount" | "design_showcase" | "inventory_clearance";
export type Channel = "eddm" | "digital" | "email" | "sms";

export interface Flyer {
  flyer_id: string;
  month: number;
  slug: string;
  slogan: string | null;
  hero_service_name: string | null;
  hero_service_catalog_id: string | null;
  cross_sell_service_ids: string[] | null;
  wave: number | null;
  has_landing_page: boolean;
}

export interface CampaignOptions {
  flyers: Flyer[];
  landing_slugs: string[];
  zips: { zip: string; status: string }[];
  channels: Channel[];
  purposes: Purpose[];
  add_on_services: string[];
  discount_approvals_enabled: boolean;
  today: string;
}

/** A campaigns row as ops_save_campaign returns it. */
export interface CampaignRow {
  id: string;
  name: string;
  slug: string | null;
  zip_code: string | null;
  start_date: string | null;
  end_date: string | null;
  status: CampaignStatus;
  purpose: Purpose | null;
  channel: Channel | null;
  flyer_id: string | null;
  campaign_type: string;
  mailer_count: number | null;
  cost_cents: number | null;
  approved_at: string | null;
  approved_by: string | null;
  created_by: string | null;
}

export interface CampaignDraftInput {
  id?: string;
  name?: string;
  slug?: string;
  flyer_id?: string | null;
  zip_code?: string;
  start_date?: string;
  end_date?: string;
  purpose?: Purpose | null;
  channel?: Channel;
}

export interface LockInput {
  campaign_id: string;
  service_id: string;
  zip?: string;
  locked_price_cents?: number;
  discount_pct?: number;
  valid_from?: string;
  valid_until?: string;
}

export type LockRefusal = "no_price" | "add_on_service" | "unavailable_whole_window" | "not_below_standard" | "unknown_service";
export type AckReason = "below_floor" | "below_cost" | "margin_not_real";

export interface LockMonth {
  month: string;
  date: string;
  available: boolean;
  reason?: string;
  standard_cents?: number;
  promo_cents?: number;
  lock_applies?: boolean;
  discount_pct?: number;
}

export interface LockEval {
  service_id: string;
  service_name: string;
  zip: string;
  valid_from: string;
  valid_until: string;
  price_cents: number | null;
  standard_cents: number | null;
  discount_pct: number;
  months: LockMonth[];
  economics: Economics | null;
  basis: Economics["basis"] | null;
  add_on: boolean;
  ack_reasons: AckReason[];
  needs_ack: boolean;
  warnings: ("partly_unavailable" | "not_below_standard_some_months")[];
  refused: LockRefusal | null;
  refused_en: string | null;
}

export interface LockPreview {
  campaign_id: string;
  campaign_status: CampaignStatus;
  eval: LockEval;
}

export interface AckNeeded {
  service_id: string;
  service_name: string;
  reasons: AckReason[];
  oficial_margin_pct: number | null;
  margin_pct: Record<Tier, number | null>;
}

export interface CampaignReview {
  campaign: CampaignRow;
  locks: LockEval[];
  acks_required: AckNeeded[];
  totals: {
    routes: number;
    addresses: number;
    conversion_pct: number;
    projected_conversions: number;
    avg_promo_price_cents: number | null;
    avg_margin_cents_oficial: number | null;
    projected_revenue_cents: number | null;
    projected_margin_cents_oficial: number | null;
    cost_cents: number | null;
    break_even_conversions: number | null;
  };
  discount_approvals_enabled: boolean;
  blockers: string[];
  can_approve: boolean;
}

export interface LockWarning {
  campaign_id: string;
  campaign_name: string;
  service_id: string;
  service_name: string;
  zip: string;
  month: string;
  lock_cents: number;
  standard_before_cents: number | null;
  standard_after_cents: number | null;
  reason: "standard_at_or_below_lock" | "unavailable";
}

export const PURPOSE_LABEL: Record<Purpose, string> = {
  service_discount: "Discount",
  design_showcase: "Showcase",
  inventory_clearance: "Clearance",
};

export const CHANNEL_LABEL: Record<Channel, string> = { eddm: "Mail (EDDM)", digital: "Digital", email: "Email", sms: "Text" };

export const ACK_LABEL: Record<AckReason, string> = {
  below_floor: "below the margin floor at the Oficial rate",
  below_cost: "below cost for at least one tier",
  margin_not_real: "margin is estimated, not from a cost profile",
};

export const TIER_LABEL: Record<Tier, string> = { aprendiz: "Aprendiz", oficial: "Oficial", maestro: "Maestro" };

/** SR-B2: names of the Lit Up tracks. */
export const TRACK_LABEL: Record<string, string> = { new: "New lights", own: "Own lights" };

/** SR-B2: economics of one track, or null when it has no price. */
export function trackEconomics(v: ServiceVariant): Economics | null {
  return v.economics && !("error" in v.economics) ? v.economics : null;
}

// 00040 §6–7 (HSC-01): catalog integrity check + catalog export
export interface IntegrityItem { severity: "error" | "warning"; check_id: string; check_name: string; target: string; detail: string }
export interface IntegrityReport {
  as_of: string; items: IntegrityItem[]; errors: number; warnings: number;
  last_run: { run_at: string; errors: number; warnings: number } | null;
}
export interface CatalogExport { exported_at: string; row_count: number; rows: Record<string, unknown>[] }

// HSC-01 (#31): quotes, pruning plans and refunds
export interface QuoteRow {
  quote_number: string; source: string; product_key: string | null; status: string; total_cents: number;
  created_at: string; expires_at: string; zip: string | null; contact: { name?: string; email?: string; phone?: string } | null;
  address: Record<string, unknown> | null; photos_needed: number; photos_received: number; obra_job_id: string | null;
  payment_status: string | null; job_status: string | null; scheduled_date: string | null; parent_quote_number: string | null; refunded_cents: number;
}
export interface QuotesOverview { as_of: string; counts: Record<string, number> | null; by_source: Record<string, number> | null; paid_total_cents: number; open_total_cents: number; quotes: QuoteRow[] }
export interface QuoteDetail {
  ok: boolean; quote_number: string; photos: string[]; pay_url: string;
  lines: { line_no: number; description: string; quantity: number; unit: string; unit_price_cents: number; line_total_cents: number }[] | null;
  addons: { quote_number: string; status: string; total_cents: number }[] | null;
  refunds: { id: string; amount_cents: number; status: string; error: string | null }[] | null;
}
export interface PlansOverview {
  as_of: string; maps_without_plan: number;
  plans: { plan_id: string; status: string; level: string; per_visit_cents: number; annual_work_cents: number; credit_remaining_cents: number;
    start_month: string; price_valid_until: string; customer: { name?: string; email?: string; phone?: string } | null; zip: string; signup_url: string;
    visits: { visit_no: number; month: string; status: string; quote_number: string | null }[] | null }[];
  open_maps: { map_id: string; created_at: string; zip: string; customer: string | null; signup_url: string; credit_until: string }[];
}
export interface RefundsOverview {
  as_of: string; counts: Record<string, number> | null;
  refunds: { id: string; quote_number: string; amount_cents: number; status: string; reason: string; error: string | null;
    stripe_refund_id: string | null; created_at: string; processed_at: string | null; customer: string | null }[];
}
